/*
  custom-engraving-text-field.js: behaviour for blocks/product_engraving-text-field.liquid.
  The engraving surcharge is a variant ("Custom Engraving (+$6)"), so the text must travel as a
  line item property on that variant only, and must not be empty when the shopper pays for it.
  Shrine's main.js is a remote script, so everything hooks in from outside it:
  - Variant changes: Shrine sets the form's input[name="id"] and dispatches a bubbling "change".
  - The input joins the <form> with form="product-form-<section>" and is named only while an
    engraving variant is selected. Shrine posts FormData(form) for one item and collects named
    properties[...] inputs in product-info for several, so "No Engraving" sends no property.
  - Add to cart: a capture-phase submit listener on document runs before Shrine's handler on the
    <form> and stops an empty (or blank) text with an inline error. Section add to cart buttons
    ([id^="SectionAtcBtn-"], layout/theme.liquid) call handleSubmit on click with no submit
    event, so their clicks get the same check.
  - Buy it now / wallet buttons fire no submit event: while the text is empty they are inert and
    a transparent guard over them shows the error. Once filled they read the form, so the
    property reaches checkout.
  - The form and id input are looked up on use: the Theme Editor can re-render the buy buttons.
*/
(() => {
  if (customElements.get('custom-engraving-text-field')) return;

  class EngravingTextField extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.errorEl = this.querySelector('[data-engraving-error]');
      this.formId = this.input ? this.input.getAttribute('form') : null;
      if (!this.input || !this.errorEl || !this.formId || !this.idInput) {
        console.warn('Engraving text field: product form not found, the field stays inactive.');
        return;
      }

      this.propertyName = this.dataset.propertyName;
      this.variantIds = new Set((this.dataset.variantIds || '').split(',').filter(Boolean));
      this.designMode = this.dataset.designMode === 'true';
      this.baseDescribedBy = this.input.getAttribute('aria-describedby') || '';
      this.lockedWrapper = null;
      this.listeners = [];

      this.listen(document, 'change', (event) => {
        if (event.target === this.idInput) this.sync();
      });
      this.listen(document, 'submit', (event) => {
        if (event.target === this.form) this.onSubmit(event);
      }, true);
      this.listen(document, 'click', (event) => {
        const sectionButton = event.target.closest && event.target.closest('[id^="SectionAtcBtn-"]');
        const productForm = this.productForm;
        if (sectionButton && productForm && productForm.dataset.main === 'true') this.onSubmit(event);
      }, true);
      this.listen(this.input, 'input', () => {
        if (this.errorShown && this.isFilled()) this.setError(false);
        this.updateCheckoutGuard();
      });
      // Buy it now reads the field as typed and never passes through onSubmit: tidy it on change.
      this.listen(this.input, 'change', () => this.trimValue());
      // Enter would submit the product form (the input belongs to it): confirm the text instead.
      this.listen(this.input, 'keydown', (event) => {
        if (event.key === 'Enter' && !event.isComposing) {
          event.preventDefault();
          this.input.blur();
        }
      });

      this.sync();
    }

    disconnectedCallback() {
      (this.listeners || []).forEach(([target, type, handler, capture]) => target.removeEventListener(type, handler, capture));
      this.listeners = [];
      this.setCheckoutLock(false);
    }

    listen(target, type, handler, capture = false) {
      target.addEventListener(type, handler, capture);
      this.listeners.push([target, type, handler, capture]);
    }

    get form() {
      return this.formId ? document.getElementById(this.formId) : null;
    }

    get productForm() {
      const form = this.form;
      return form ? form.closest('product-form') : null;
    }

    get idInput() {
      const form = this.form;
      return form ? form.querySelector('input[name="id"]') : null;
    }

    isActive() {
      const idInput = this.idInput;
      return !!idInput && this.variantIds.has(String(idInput.value));
    }

    isFilled() {
      return this.input.value.trim().length > 0;
    }

    trimValue() {
      const text = this.input.value.trim();
      if (text !== this.input.value) {
        this.input.value = text;
        this.updateCheckoutGuard();
      }
    }

    sync() {
      const active = this.isActive();
      this.hidden = !active && !this.designMode;
      if (active) {
        this.input.setAttribute('name', this.propertyName);
        this.input.setAttribute('aria-required', 'true');
      } else {
        this.input.removeAttribute('name');
        this.input.removeAttribute('aria-required');
        this.setError(false);
      }
      this.updateCheckoutGuard();
    }

    onSubmit(event) {
      if (!this.isActive()) return;
      this.trimValue();
      if (this.input.value) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      this.setError(true);
      this.focusField();
    }

    setError(show) {
      this.errorShown = show;
      this.errorEl.hidden = !show;
      this.input.classList.toggle('custom-engraving-text-field__input--error', show);
      if (show) {
        this.input.setAttribute('aria-invalid', 'true');
        this.input.setAttribute('aria-describedby', `${this.baseDescribedBy} ${this.errorEl.id}`.trim());
      } else {
        this.input.removeAttribute('aria-invalid');
        if (this.baseDescribedBy) this.input.setAttribute('aria-describedby', this.baseDescribedBy);
        else this.input.removeAttribute('aria-describedby');
      }
    }

    focusField() {
      // Centre the field so the floating header never covers it, then focus without a second jump.
      this.scrollIntoView({ block: 'center', behavior: 'smooth' });
      this.input.focus({ preventScroll: true });
    }


    updateCheckoutGuard() {
      this.setCheckoutLock(this.isActive() && !this.isFilled());
    }

    setCheckoutLock(lock) {
      const productForm = this.productForm;
      const wrapper = lock && productForm ? productForm.querySelector('.shopify-payment-button') : null;
      if (wrapper === this.lockedWrapper) return;

      // Release whatever is locked now (it may be a wrapper the editor already replaced).
      if (this.lockedWrapper) {
        if (this.walletObserver) this.walletObserver.disconnect();
        this.walletObserver = null;
        if (this.guard) this.guard.remove();
        this.lockedWrapper.classList.remove('custom-engraving-checkout-locked');
        this.applyInert(this.lockedWrapper, false);
        this.lockedWrapper = null;
      }
      if (!wrapper) return;

      if (!this.guard) {
        this.guard = document.createElement('button');
        this.guard.type = 'button';
        this.guard.className = 'custom-engraving-checkout-guard';
        // Pointer-only: keyboard users cannot reach the inert buttons, and add to cart explains.
        this.guard.tabIndex = -1;
        this.guard.setAttribute('aria-hidden', 'true');
        this.guard.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.isFilled()) return this.updateCheckoutGuard();
          this.setError(true);
          this.focusField();
        });
      }
      wrapper.classList.add('custom-engraving-checkout-locked');
      wrapper.appendChild(this.guard);
      this.applyInert(wrapper, true);
      // Shopify can render the wallet buttons after this runs: keep new children inert too.
      this.walletObserver = new MutationObserver(() => this.applyInert(wrapper, true));
      this.walletObserver.observe(wrapper, { childList: true });
      this.lockedWrapper = wrapper;
    }

    applyInert(wrapper, inert) {
      Array.from(wrapper.children).forEach((child) => {
        if (child !== this.guard) child.inert = inert;
      });
    }
  }

  customElements.define('custom-engraving-text-field', EngravingTextField);
})();
